/* eslint-disable no-restricted-syntax */
/* eslint-disable import/no-anonymous-default-export */
import request from 'superagent';
import { DOM, isServer } from '@app/utils';
import decode from 'jwt-decode';
import { getProp } from '@app/redux/utils/get-prop';
import { LANGUAGES } from '@app/i18n/data/languages';
import { getOrSetStockXSessionId } from '@app/fraud';
import { config } from '@app/config';
import { isEmpty } from 'lodash';
import type { DecodedJWT } from '@app/types';

const isNotExpired = (token: string) => {
  if (!token) {
    return true;
  }

  const decoded = decode<DecodedJWT>(token);
  if (decoded?.exp) {
    return decoded.exp - Date.now() / 1000 > 10;
  }
};

const instance = null;

/**
 * @deprecated
 */
class IronLegacyClient {
  _appOS: any;
  _appVersion: any;
  _authorizationToken: any;
  _pendingRequests: any;
  _url: any;
  constructor() {
    if (instance) {
      // eslint-disable-next-line no-constructor-return
      return instance;
    }

    this._url = '/api';
    this._pendingRequests = {};

    this._authorizationToken = this._getToken();

    this._appVersion = '0.1';
    this._appOS = 'web';
  }

  static checkStockXSession() {
    if (isServer) {
      return;
    }

    return getOrSetStockXSessionId();
  }

  makeUrl(part) {
    if (part.indexOf(this._url) > -1) {
      return part;
    }

    return this._url + part;
  }

  // eslint-disable-next-line class-methods-use-this
  _getToken() {
    return DOM.getCookie('token');
  }

  // eslint-disable-next-line class-methods-use-this
  _getCookie(name) {
    const cookie = DOM.getCookie(name) || false;
    if (cookie && cookie !== 'null') {
      return cookie;
    }
  }

  abortPendingRequest(key) {
    if (this._pendingRequests[key]) {
      this._pendingRequests[key].abort();
      this._pendingRequests[key] = null;
    }
  }

  async performRefreshFlow() {
    if (isNotExpired(this._authorizationToken)) {
      // eslint-disable-next-line no-promise-executor-return
      return;
    }

    const auth = (await import('auth0-js')).default;

    const webAuthClient = new auth.WebAuth({
      domain: config.SERVICES.AUTH0.DOMAIN,
      clientID: config.SERVICES.AUTH0.CLIENTID,
      responseType: config.SERVICES.AUTH0.RESPONSETYPE,
      scope: config.SERVICES.AUTH0.SCOPE,
    });

    const code = getProp(null, 'preLoadedLang.code');
    const { url } = LANGUAGES[code] || { url: 'en-us' };
    let redirectUri;
    if (typeof window !== 'undefined') {
      const { protocol, host, pathname, search } = window.location;
      const addedQuery = search ? `&${search.substring(1)}` : '';
      redirectUri = `${protocol}//${host}/callback?path=${
        pathname === '/social-confirm' ? '/' : pathname
      }${addedQuery}`;
      webAuthClient.checkSession(
        {
          redirectUri,
          connection: config.SERVICES.AUTH0.CONNECTION,
          audience: config.SERVICES.AUTH0.AUDIENCE,
          'stockx-language': url,
        },
        (err, authResults) => {
          if (err) {
            DOM.delCookie('token');
            DOM.delCookie('stockx_tos_privacy_accepted');
            DOM.delCookie('stockx_user_logged_in');
            DOM.delCookie('stockx_session');

            // /help auth cookies
            DOM.deleteHelpCenterAuthCookies();

            this._authorizationToken = null;
            const returnTo = `${protocol}//${host}/`;
            webAuthClient.logout({ returnTo });
          } else {
            DOM.setCookie('token', authResults.accessToken);
            this._authorizationToken = authResults.accessToken;
          }
        },
      );
    }
  }

  async get<T = unknown>(params, abort = true, skipAuth = false) {
    if (abort) {
      this.abortPendingRequest(params.url);
    }

    if (this._getToken() !== this._authorizationToken) {
      this._authorizationToken = this._getToken();
    }

    await this.performRefreshFlow();

    IronLegacyClient.checkStockXSession();

    return new Promise<T>((resolve, reject) => {
      this._pendingRequests[params.url] = request
        .get(this.makeUrl(params.url))
        .set('X-Requested-With', 'XMLHttpRequest')
        .set(
          'Authorization',
          this._authorizationToken && !skipAuth
            ? `Bearer ${this._authorizationToken}`
            : '',
        )
        .set('appVersion', this._appVersion)
        .set('appOS', this._appOS)
        .query(params?.data || {})
        .end((err, res) => {
          if (err) {
            reject(err);
          } else {
            resolve(<T>res);
          }
        });
    });
  }

  async post<T = unknown>(params, abort = true, additionalHeaders: Dict = {}) {
    if (abort) {
      this.abortPendingRequest(params.url);
    }

    if (this._getToken() !== this._authorizationToken) {
      this._authorizationToken = this._getToken();
    }

    const sessionId = IronLegacyClient.checkStockXSession();

    const forterTokenVal: string = DOM.getCookie('forterToken') || '';
    await this.performRefreshFlow();
    return new Promise<T>((resolve, reject) => {
      const postRequest = request
        .post(this.makeUrl(params.url))
        .set('X-Requested-With', 'XMLHttpRequest')
        .set(
          'authorization',
          this._authorizationToken ? `Bearer ${this._authorizationToken}` : '',
        )
        .set('appVersion', this._appVersion)
        .set('appOS', this._appOS)
        .set('x-session-id', sessionId ? `${sessionId}` : '')
        .set('x-fraud-cookie-id', forterTokenVal);

      if (!isEmpty(additionalHeaders)) {
        Object.entries(additionalHeaders).forEach(
          ([headerKey, headerValue]) => {
            postRequest.set(headerKey, headerValue);
          },
        );
      }

      postRequest.send(params.data).end((err, res) => {
        if (err) {
          reject(err);
        } else {
          resolve(<T>res);
        }
      });

      this._pendingRequests[params.url] = postRequest;
    });
  }

  async put<T = unknown>(params, additionalHeaders: Dict = {}) {
    this.abortPendingRequest(params.url);

    if (this._getToken() !== this._authorizationToken) {
      this._authorizationToken = this._getToken();
    }

    await this.performRefreshFlow();
    IronLegacyClient.checkStockXSession();

    return new Promise<T>((resolve, reject) => {
      const putRequest = request
        .put(this.makeUrl(params.url))
        .set('X-Requested-With', 'XMLHttpRequest')
        .set(
          'authorization',
          this._authorizationToken ? `Bearer ${this._authorizationToken}` : '',
        )
        .set('appVersion', this._appVersion)
        .set('appOS', this._appOS);

      if (!isEmpty(additionalHeaders)) {
        Object.entries(additionalHeaders).forEach(
          ([headerKey, headerValue]) => {
            putRequest.set(headerKey, headerValue);
          },
        );
      }

      putRequest.send(params.data).end((err, res) => {
        if (err) {
          reject(err);
        } else {
          resolve(<T>res);
        }
      });

      this._pendingRequests[params.url] = putRequest;
    });
  }

  async del(params, abort = true) {
    if (abort) {
      this.abortPendingRequest(params.url);
    }

    if (this._getToken() !== this._authorizationToken) {
      this._authorizationToken = this._getToken();
    }

    await this.performRefreshFlow();
    IronLegacyClient.checkStockXSession();
    return new Promise((resolve, reject) => {
      this._pendingRequests[params.url] = request
        .del(this.makeUrl(params.url))
        .set('X-Requested-With', 'XMLHttpRequest')
        .set(
          'authorization',
          this._authorizationToken ? `Bearer ${this._authorizationToken}` : '',
        )
        .set('appVersion', this._appVersion)
        .set('appOS', this._appOS)
        .send(params.data)
        .end((err, res) => {
          if (err) {
            reject(err);
          } else {
            resolve(res);
          }
        });
    });
  }

  requestCustomerCSV(id, status, type) {
    IronLegacyClient.checkStockXSession();
    return this.get({
      url: `/customers/${id}/${type}/report?status=${status}`,
    });
  }

  postBillingUpdate(billing) {
    return this.post({ url: '/billing', data: billing });
  }

  postShippingUpdate(shipping) {
    return this.post({ url: '/shipping', data: shipping });
  }

  postMerchantUpdate<T = unknown>(id, merchant, vaultTraceId) {
    return this.put<T>(
      { url: `/merchants/${id}`, data: merchant },
      {
        'x-vault-trace-id': vaultTraceId,
      },
    );
  }

  unsubscribeNotification(notificationId, userUuid) {
    return this.put({
      url: `/notifications/unsubscribe/${userUuid}/${notificationId}`,
    });
  }

  getProduct(id, currency = 'USD', country, marketName) {
    const marketString = marketName ? `&market=${marketName}` : '';
    return this.get({
      url: `/products/${id}?includes=market,360&currency=${currency}${marketString}`,
      data: { country },
    });
  }

  getProductSorts() {
    return this.get({ url: '/products/sorts?all=true' });
  }

  getPortfolioItems(url) {
    // by default the get function will abort any call made with the same url
    // This additional check will abort any calls where the only difference is the query parameters at the end
    for (const key in this._pendingRequests) {
      if (
        url.indexOf('?') !== -1 &&
        key.indexOf('?') !== -1 &&
        url.startsWith(key.substr(0, key.lastIndexOf('?') + 1))
      ) {
        this.abortPendingRequest(key);
      }
    }

    return this.get({ url });
  }

  getPortfolioItem<T>(chainId, params, marketName = '', country = '') {
    const query = params ? `?includes=${params.join(',')}` : '?';
    const marketString = marketName ? `&market=${marketName}` : '';
    const countryString = country ? `&country=${country}` : '';
    return this.get<T>({
      url: `/portfolioItems/${chainId}${query}${marketString}${countryString}`,
    });
  }

  getCustomerPortfolioItems({
    customerId,
    type,
    status,
    limit = 0,
    page = 1,
    sort = 'created_at',
    order = 'DESC',
    filterableState = '',
    search = '',
    currency = 'USD',
    marketName = null,
    country = '',
  }) {
    let limitString = '';
    if (limit !== 0) {
      limitString = `?sort=${sort}&order=${order}`;
      limitString += `&limit=${limit}&page=${page}`;
    }

    if (filterableState !== '') {
      if (limitString === '') {
        limitString += `?state=${filterableState}`;
      } else {
        limitString += `&state=${filterableState}`;
      }
    }

    if (search !== '') {
      if (limitString === '') {
        limitString += `?q=${search}`;
      } else {
        limitString += `&q=${search}`;
      }
    }

    const currencyString = `&currency=${currency}`;
    const marketString = marketName ? `&market=${marketName}` : '';
    return this.get({
      url: `/customers/${customerId}/${type}/${status}${limitString}${currencyString}${marketString}`,
      data: { country },
    });
  }

  deletePortfolioItem(id, notes) {
    if (id === null) {
      return false;
    }

    return this.del(
      { url: `/portfolio/${id}`, data: { chain_id: id, notes } },
      false,
    );
  }

  getProductGraph(id) {
    if (id === null) {
      return false;
    }

    return this.get({ url: `/products/${id}/graph` });
  }

  postPortfolioItem(action, data) {
    if (data === null) {
      return false;
    }

    let params;

    if (action === 'ship') {
      params = { url: `/portfolioItems/${data.chainId}`, data };
    } else {
      params = { url: `/portfolio?a=${action}`, data };
    }

    return this.post(params, false);
  }

  postPortfolioItems(data) {
    if (!data) {
      return;
    }

    const params = {
      data,
      url: '/portfolio/multi?a=ask',
    };

    return this.post(params, false);
  }

  postExtendPortfolioItems(data) {
    if (data === null) {
      return false;
    }

    const params = { url: `/portfolioItems/extend`, data };

    return this.post(params, false);
  }

  postSocialConfirmRedirectUrl(payload) {
    return this.post({
      url: '/social-confirm-redirect-url',
      data: payload,
    });
  }

  updateCustomer<T = unknown>(id, data) {
    return this.put<T>({ url: `/customers/${id}`, data });
  }

  getCurrencies(isTeamMember = false) {
    return this.get({
      url: `/currencies?showInvisibleCurrencies=${isTeamMember}`,
    });
  }

  postPayment<T>({
    value,
    currency,
    chargeRef,
    captureRef,
    discountCodes,
    billingAddress,
    deviceData,
    shippingAddress,
    localizedSizeType,
    checkoutTraceId,
  }) {
    return this.post<T>(
      {
        url: '/payments',
        data: {
          amount: {
            value: String(value),
            currency,
          },
          chargeRef,
          captureRef,
          discountCodes,
          billingAddress,
          deviceData,
          shippingAddress,
          localizedSizeType,
        },
      },
      true,
      {
        'x-checkout-trace-id': checkoutTraceId,
      },
    );
  }

  getCountries(action, productCategory) {
    const params = {
      url: '/shipping/countries',
    };

    if (action) {
      params.url += `?format=ranked&context=${action}&productCategory=${productCategory}`;
    }

    return this.get(params);
  }

  getTraderRewards() {
    return this.get({ url: '/users/me/rewards' });
  }

  getTraderBuyingStats(currency = 'USD') {
    return this.get({ url: `/users/me/buying?currency=${currency}` });
  }

  getTraderSellingStats(currency = 'USD') {
    return this.get({ url: `/users/me/selling?currency=${currency}` });
  }

  getMe() {
    return this.get({ url: '/users/me?meta=include' });
  }

  getMfaEnrollmentStatus() {
    return this.get({ url: '/mfa' });
  }

  getPromoRaised(urlKey) {
    return this.get({ url: `/promos/raised?url=${urlKey}` });
  }

  getAllBrowseFilters() {
    return this.get({ url: '/allBrowseFilters' });
  }

  getAllCountries() {
    return this.get({ url: '/countries' });
  }

  confirmPayoutMethodChange(payload) {
    return this.post({
      url: '/confirmation/confirm',
      data: payload,
    });
  }

  resendPayoutVerificationConfirmation(payload) {
    return this.post({
      url: '/confirmation/resendConfirmationEmail',
      data: payload,
    });
  }
}

export default new IronLegacyClient();
